import { useState, useEffect, useRef, useCallback } from 'react';
import { usePollingAggregatedEvents } from '../../hooks/useAggregatedEvents';
import { useWebSocket } from '../../hooks/useWebSocket';
import { UI_TIMINGS } from '../../config/appConfig';

// WebSocket message types
const MESSAGE_TYPE = {
  EVENT_UPDATE: 'EVENT_UPDATE'
};

// DynamoDB Stream operations
const OPERATION = {
  INSERT: 'INSERT',
  MODIFY: 'MODIFY',
  REMOVE: 'REMOVE'
};

/**
 * Hybrid Event Source Hook
 *
 * Uses WebSocket for real-time event updates with automatic fallback to HTTP polling.
 *
 * - Primary: Events arrive in real-time via WebSocket
 * - Fallback: If WebSocket fails repeatedly (3+ times), switches to HTTP polling
 * - Initial load: Fetches existing events via HTTP, then switches to WebSocket
 *
 * API matches useEventPolling exactly - drop-in replacement.
 */
export function useHybridEventSource({
  events,
  setEvents,
  setError,
  setLoading,
  pollInterval,
  pageSize,
  enabled,
  setNextToken,
  setHasMore,
  hasPaginated,
  onNewEvent // Optional callback for immediate event notifications (e.g., ticker)
}) {
  const [lastPoll, setLastPoll] = useState(null);
  const [hasNewEvents, setHasNewEvents] = useState(false);
  const [newEventCount, setNewEventCount] = useState(0);
  const [isPulsing, setIsPulsing] = useState(false);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [usePollingFallback, setUsePollingFallback] = useState(false);
  const lastEventIdRef = useRef(null);
  const hasPaginatedRef = useRef(hasPaginated);

  // Keep ref in sync with prop
  useEffect(() => {
    hasPaginatedRef.current = hasPaginated;
  }, [hasPaginated]);

  // Check if we should force polling fallback due to WebSocket failures
  // After 3 consecutive failures, fallback to HTTP polling
  useEffect(() => {
    const MAX_FAILURES = 3;
    if (consecutiveFailures >= MAX_FAILURES) {
      if (!usePollingFallback) {
        console.warn(`WebSocket failed ${consecutiveFailures} times, falling back to HTTP polling`);
        setUsePollingFallback(true);
      }
    }
  }, [consecutiveFailures, usePollingFallback]);

  // Determine which transport to use
  // WebSocket is always enabled unless fallback to polling is triggered
  const useWebSocketTransport = !usePollingFallback && enabled;
  const usePollingTransport = !useWebSocketTransport && enabled;

  // Debug logging
  useEffect(() => {
    console.log('[HybridEventSource] Config:', {
      usePollingFallback,
      enabled,
      useWebSocketTransport,
      usePollingTransport,
      consecutiveFailures
    });
  }, [usePollingFallback, enabled, useWebSocketTransport, usePollingTransport, consecutiveFailures]);

  // ============================================================================
  // WEBSOCKET TRANSPORT
  // ============================================================================

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[useHybridEventSource] WebSocket message received:', message.type, message.operation);

    // Only handle EVENT_UPDATE messages
    if (message.type !== MESSAGE_TYPE.EVENT_UPDATE) {
      console.log('[useHybridEventSource] Ignoring non-EVENT_UPDATE message');
      return;
    }

    const newEvent = {
      ...message.data,
      operation: message.operation,
      receivedAt: message.timestamp
    };

    console.log('[useHybridEventSource] Processing event:', newEvent.eventType, newEvent.eventId?.substring(0, 15), 'operation:', message.operation);

    // Update events list
    setEvents(prevEvents => {
      const existingIndex = prevEvents.findIndex(e => e.eventId === newEvent.eventId);

      // REMOVE: Delete existing event
      if (message.operation === OPERATION.REMOVE) {
        console.log('[useHybridEventSource] REMOVE operation, existingIndex:', existingIndex);
        if (existingIndex === -1) {
          // Event doesn't exist, nothing to remove
          return prevEvents;
        }

        const updatedEvents = prevEvents.filter((_, index) => index !== existingIndex);

        // Update last poll indicator
        setLastPoll(new Date());
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), UI_TIMINGS.POLL_PULSE_DURATION);

        return updatedEvents;
      }

      // MODIFY: Update existing event
      if (message.operation === OPERATION.MODIFY && existingIndex !== -1) {
        console.log('[useHybridEventSource] MODIFY operation, updating at index:', existingIndex);
        const updatedEvents = [...prevEvents];
        updatedEvents[existingIndex] = newEvent;

        // Update last poll indicator (but don't show "new event" indicator)
        setLastPoll(new Date());
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), UI_TIMINGS.POLL_PULSE_DURATION);

        return updatedEvents;
      }

      // INSERT: Skip if we already have this event
      if (existingIndex !== -1) {
        console.log('[useHybridEventSource] INSERT - event already exists, skipping');
        return prevEvents;
      }

      // INSERT: Add new event to beginning
      console.log('[useHybridEventSource] INSERT - adding new event to beginning');
      const updatedEvents = [newEvent, ...prevEvents];

      // Track new event
      setNewEventCount(1);
      setHasNewEvents(true);
      setTimeout(() => setHasNewEvents(false), UI_TIMINGS.NEW_EVENT_INDICATOR);

      // Update last poll indicator
      setLastPoll(new Date());
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), UI_TIMINGS.POLL_PULSE_DURATION);

      // Reset failure count and clear errors on successful event
      setConsecutiveFailures(0);
      setError(null);

      // Notify callback immediately for ticker/notifications
      if (onNewEvent) {
        console.log('[useHybridEventSource] Calling onNewEvent callback for ticker');
        onNewEvent(newEvent);
      } else {
        console.log('[useHybridEventSource] No onNewEvent callback registered');
      }

      return updatedEvents;
    });
  }, [setEvents, onNewEvent]);

  // WebSocket connection state handlers
  const handleWebSocketConnect = useCallback(() => {
    console.log('[HybridEventSource] WebSocket connected');
    setConsecutiveFailures(0);
    setLoading(false);
  }, [setLoading]);

  const handleWebSocketDisconnect = useCallback(() => {
    console.log('[HybridEventSource] WebSocket disconnected');
  }, []);

  const handleWebSocketError = useCallback((error) => {
    console.error('[HybridEventSource] WebSocket error:', error);
    setConsecutiveFailures(prev => prev + 1);
    setError(`WebSocket connection issue. Attempting to reconnect...`);
  }, [setError]);

  const handleAuthExpired = useCallback((data) => {
    console.error('[HybridEventSource] Auth expired - forcing logout');

    // Import dynamically to avoid circular dependencies
    import('../../services/auth/authService').then(({ signOut }) => {
      signOut().then(() => {
        // Reload page to trigger Amplify auth redirect
        window.location.reload();
      });
    });
  }, []);

  // Initialize WebSocket connection
  const { connectionState: wsStatus, error: connectionError, reconnect: wsReconnect } = useWebSocket({
    onMessage: handleWebSocketMessage,
    onConnect: handleWebSocketConnect,
    onDisconnect: handleWebSocketDisconnect,
    onError: handleWebSocketError,
    onAuthExpired: handleAuthExpired,
    enabled: useWebSocketTransport
  });

  // Handle WebSocket connection errors
  useEffect(() => {
    if (connectionError && useWebSocketTransport) {
      setError(`WebSocket connection failed: ${connectionError}`);
    }
  }, [connectionError, useWebSocketTransport, setError]);

  // ============================================================================
  // HTTP POLLING TRANSPORT
  // ============================================================================

  // Track if we've done initial load for WebSocket mode
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Use React Query for polling OR initial load
  // When using WebSocket: fetch once initially, then disable polling
  // When using HTTP polling: continuously poll
  const shouldPoll = usePollingTransport || (useWebSocketTransport && !initialLoadComplete);
  const { data, error: queryError, isLoading, dataUpdatedAt } = usePollingAggregatedEvents({
    limit: pageSize,
    enabled: shouldPoll,
    refetchInterval: usePollingTransport ? pollInterval : false // Only poll if using HTTP transport
  });

  // Process polling data when it updates
  useEffect(() => {
    if (!data) return;

    // Clear any previous errors on successful fetch
    setError(null);

    // Mark initial load as complete for WebSocket mode
    if (useWebSocketTransport && !initialLoadComplete) {
      setInitialLoadComplete(true);
      console.log('[HybridEventSource] Initial load complete, switching to WebSocket-only mode');
    }

    // Skip processing if we're in WebSocket mode and already loaded
    if (useWebSocketTransport && initialLoadComplete) return;

    const newEvents = data.events || [];
    let hasChanges = false;

    // Check for new events
    if (newEvents.length > 0) {
      const latestEventId = newEvents[0]?.eventId;

      if (lastEventIdRef.current && lastEventIdRef.current !== latestEventId) {
        const lastIndex = newEvents.findIndex(e => e.eventId === lastEventIdRef.current);
        const count = lastIndex === -1 ? newEvents.length : lastIndex;
        setNewEventCount(count);
        setHasNewEvents(true);
        setTimeout(() => setHasNewEvents(false), UI_TIMINGS.NEW_EVENT_INDICATOR);
        hasChanges = true;
      }

      lastEventIdRef.current = latestEventId;
    }

    // Update nextToken and hasMore for pagination
    if (!hasPaginatedRef.current) {
      if (setNextToken) {
        setNextToken(data.nextToken || null);
      }
      if (setHasMore) {
        setHasMore(data.hasMore);
      }
    }

    // Update events if changed
    setEvents(prevEvents => {
      // If paginated, only prepend new events
      if (prevEvents.length > pageSize) {
        const existingIds = new Set(prevEvents.map(e => e.eventId));
        const brandNewEvents = newEvents.filter(e => !existingIds.has(e.eventId));

        if (brandNewEvents.length > 0) {
          setNewEventCount(brandNewEvents.length);
          setHasNewEvents(true);
          setTimeout(() => setHasNewEvents(false), UI_TIMINGS.NEW_EVENT_INDICATOR);
          hasChanges = true;
        }

        return [...brandNewEvents, ...prevEvents];
      }

      // No pagination - check if events actually changed
      if (prevEvents.length === newEvents.length && prevEvents.length > 0) {
        if (newEvents[0]?.eventId === prevEvents[0]?.eventId) {
          return prevEvents; // No change - don't update
        }
      }

      // Events changed
      hasChanges = true;
      return newEvents;
    });

    // Only update poll indicator if there were actual changes
    if (hasChanges) {
      setLastPoll(new Date(dataUpdatedAt));
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), UI_TIMINGS.POLL_PULSE_DURATION);
    }
  }, [usePollingTransport, useWebSocketTransport, initialLoadComplete, data, dataUpdatedAt, pageSize, setEvents, setNextToken, setHasMore]);

  // Handle loading state
  useEffect(() => {
    // Show loading during initial fetch (both modes) or during polling
    if (usePollingTransport || (useWebSocketTransport && !initialLoadComplete)) {
      setLoading(isLoading);
    }
  }, [usePollingTransport, useWebSocketTransport, initialLoadComplete, isLoading, setLoading]);

  // Handle polling errors
  useEffect(() => {
    if (usePollingTransport && queryError) {
      setError(queryError.message || 'Failed to fetch events');
    }
  }, [usePollingTransport, queryError, setError]);

  // ============================================================================
  // RETURN
  // ============================================================================

  // Simple reconnect: reset fallback and trigger WebSocket reconnect
  const retryWebSocket = () => {
    if (usePollingFallback) {
      console.log('[HybridEventSource] Resetting polling fallback to retry WebSocket');
      setUsePollingFallback(false);
      setConsecutiveFailures(0);
      // Force reconnect after state updates
      setTimeout(() => {
        if (wsReconnect) {
          console.log('[HybridEventSource] Calling wsReconnect after reset');
          wsReconnect();
        }
      }, 100);
    } else {
      // Already in WebSocket mode, just reconnect
      if (wsReconnect) {
        console.log('[HybridEventSource] Already in WebSocket mode, reconnecting');
        wsReconnect();
      }
    }
  };

  return {
    lastPoll,
    hasNewEvents,
    newEventCount,
    isPulsing,
    // Debug info (not part of original API but useful)
    _transport: useWebSocketTransport ? 'websocket' : 'polling',
    _wsStatus: wsStatus,
    _wsReconnect: retryWebSocket
  };
}
